import { AuthError, AUTH_ERROR_CODE } from '@shared/auth/errors';
import { clearImpersonation } from '@shared/auth/impersonation';
import { requestRefresh } from '@shared/auth/refresh-request';
import { tokenStore, isImpersonating, isTokenValid, markLoggedOut } from '@shared/auth/token.store';
import type { User } from '@shared/types/auth';

// ── Public auth routes ──────────────────────────────────────────────────────
// Unauthenticated-by-design pages. A failed silent refresh must NOT bounce the
// user off these (e.g. someone opening a password-reset link has no session).
// A Connect's sign-in tab ends on the integrations complete page, which reports
// and closes itself whether or not the tab restores a session.
const PUBLIC_AUTH_PATHS = [
  '/login',
  '/forgot-password',
  '/check-email',
  '/reset-password',
  '/settings/integrations/complete',
];

/** True when the current location is a public auth page that should not redirect to /login. */
export function isOnPublicAuthPath(): boolean {
  return PUBLIC_AUTH_PATHS.includes(window.location.pathname);
}

// ── Redirect ──────────────────────────────────────────────────────────────────

/**
 * Sanitize a post-login redirect target taken from the `url` query param.
 * Only SAME-ORIGIN relative paths are allowed; absolute URLs, protocol-relative
 * `//host`, and `javascript:`/`data:` schemes resolve to a different origin and
 * are rejected — otherwise `/login?url=https://evil.com` could bounce a user
 * off-site after a silent refresh (open redirect). Returns a safe path or the
 * fallback. (Input from URLSearchParams.get is already percent-decoded.)
 */
export function safeRedirectPath(raw: string | null | undefined, fallback = '/'): string {
  if (!raw) return fallback;
  try {
    const u = new URL(raw, window.location.origin);
    if (u.origin !== window.location.origin) return fallback;
    const path = `${u.pathname}${u.search}${u.hash}`;
    return path.startsWith('/') && !path.startsWith('//') ? path : fallback;
  } catch {
    return fallback;
  }
}

export function redirectToLogin(clear?: boolean): void {
  const impersonating = isImpersonating();
  if (impersonating) clearImpersonation();
  // Already on a public auth page (recovery/reset/etc.) — don't bounce away.
  if (isOnPublicAuthPath()) {
    if (clear) tokenStore.getState().clearAccessToken();
    return;
  }
  if (clear || impersonating) {
    if (clear) tokenStore.getState().clearAccessToken();
    window.location.replace('/login');
  } else {
    // The page's path AND query, so a deep link that carries its parameters
    // (/sso/trade-system?redirect_uri=…) comes back with them after the login.
    const url = `${window.location.pathname}${window.location.search}`;
    const params = new URLSearchParams({ redirect: 'true', url });
    window.location.replace(`/login?${params.toString()}`);
  }
}

// ── Token refresh (deduplicated via the shared refresh-request module) ────────

export function fetchAccessToken(): Promise<Response> {
  return requestRefresh();
}

export interface RefreshedSession {
  user?: Partial<User>;
}

export async function refetchToken(): Promise<RefreshedSession> {
  if (isImpersonating() && isTokenValid()) return {};

  const res = await fetchAccessToken();

  if (res.status === 401 || res.status === 403) {
    // Redirect AND throw. Returning normally here made every caller believe the
    // refresh had succeeded: AuthProvider went on to fire GET_CURRENT_USER with
    // no access token, the server answered "Access denied", and error.exchange
    // showed the user a warning toast on the login screen itself. The
    // UNAUTHENTICATED branches in AuthProvider and useTokenRefresh were written
    // for this case and were unreachable until now.
    redirectToLogin();
    throw new AuthError(AUTH_ERROR_CODE.UNAUTHENTICATED);
  }

  if (!res.ok) {
    throw new AuthError(AUTH_ERROR_CODE.SERVER_UNAVAILABLE);
  }

  const data = (await res.json()) as {
    token?: string;
    accessTokenTTL?: number;
    user?: Partial<User>;
  };

  if (!data.token) {
    throw new AuthError(AUTH_ERROR_CODE.TOKEN_NOT_RECEIVED);
  }

  tokenStore.getState().setAccessToken(data.token, data.accessTokenTTL);

  if (window.location.pathname === '/login') {
    const urlParam = new URLSearchParams(window.location.search).get('url');
    if (urlParam) {
      // Validate against open redirect — only same-origin relative paths.
      window.location.replace(safeRedirectPath(urlParam));
    }
  }

  return { user: data.user };
}

// ── Logout ────────────────────────────────────────────────────────────────────

// DEV-3506: AI CIO conversation history is persisted in localStorage. Wipe it
// on logout so it can't surface for the next user on a shared browser — covers
// the per-user-scoped keys (`investbanq-ai-cio:<userId>`) and the legacy
// unscoped `investbanq-ai-cio` key. The store is additionally user-scoped at
// write time (see conversations.store), so this is defense in depth.
const USER_SCOPED_STORAGE_PREFIXES = ['investbanq-ai-cio', 'investbanq-settings-ai'];

function clearAiCioStorage(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && USER_SCOPED_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix)))
        keys.push(key);
    }
    keys.forEach((k) => {
      localStorage.removeItem(k);
    });
  } catch {
    // ignore private-mode / quota
  }
}

export async function logout(): Promise<void> {
  if (isImpersonating()) {
    redirectToLogin(true);
    return;
  }

  // Set this FIRST: it stops every silent refresher (router bootstrap, the
  // 60s poll, the urql/ws exchanges) from re-minting an access token off the
  // still-valid httpOnly refresh cookie while the DELETE below is in flight.
  markLoggedOut();

  // Always attempt server-side revocation, even if the local access token has
  // already expired — the refresh-token cookie may still be valid, and
  // skipping this call left it (and the server-side session) alive.
  try {
    await fetch('/api/logout', {
      method: 'DELETE',
      credentials: 'include',
      headers: isTokenValid()
        ? { Authorization: `Bearer ${tokenStore.getState().accessToken?.token ?? ''}` }
        : {},
    });
  } catch {
    // best-effort — httpOnly cookie can't be cleared from JS if this fails,
    // but markLoggedOut() above still prevents this client from silently
    // resurrecting the session.
  }

  clearAiCioStorage();
  redirectToLogin(true);
}
